import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import simccLogo from "../assets/images/simcc-logo-128.png";
import {
  isAdmin,
  isAdminOrPartnerOrAssistant,
  isAdminOrPartnerOrAssistantOrManager,
} from "../functions/checkrole";
import { Drawer, DrawerHeader } from "./DrawerAndAppBar";
import "./NavigationShell.css";

const getNavigationSections = () => [
  {
    id: "dashboard",
    label: "Overview",
    solo: true,
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: <DashboardRoundedIcon />,
      },
    ],
  },
  {
    id: "competitions",
    label: "Competitions",
    items: [
      isAdminOrPartnerOrAssistant() && {
        label: "All Competitions",
        path: "/competitions",
        icon: <EmojiEventsOutlinedIcon />,
      },
      isAdmin() && {
        label: "Team Competitions",
        path: "/teamcompetitions",
        icon: <GroupsOutlinedIcon />,
      },
    ],
  },
  {
    id: "content-bank",
    label: "Content Bank",
    items: [
      isAdmin() && {
        label: "Tasks",
        path: "/task",
        icon: <ListAltOutlinedIcon />,
      },
      isAdmin() && {
        label: "Collections",
        path: "/collection",
        icon: <CollectionsBookmarkOutlinedIcon />,
      },
      {
        label: "Translations",
        icon: <TranslateOutlinedIcon />,
        children: [
          { label: "Assign Translation", path: "/assigntranslation" },
          { label: "Translate", path: "/translate" },
          {
            label: "Download Contest Paper",
            path: "/downloadcontestpaper",
          },
        ],
      },
      isAdmin() && {
        label: "Domains & Tags",
        path: "/domain",
        icon: <LanguageOutlinedIcon />,
      },
      isAdmin() && {
        label: "Difficulty Groups",
        path: "/difficultygroup",
        icon: <TuneOutlinedIcon />,
      },
    ],
  },
  {
    id: "participants",
    label: "Participants",
    items: [
      {
        label: "Participants",
        path: "/participants",
        icon: <PeopleAltOutlinedIcon />,
      },
      {
        label: "Imports",
        path: "/uploadedparticipantsfiles",
        icon: <UploadFileOutlinedIcon />,
      },
    ],
  },
  {
    id: "certificates",
    label: "Certificates",
    items: [
      isAdmin() && {
        label: "Templates",
        path: "/certificatetemplates",
        icon: <CardMembershipOutlinedIcon />,
      },
      {
        label: "Print Certificates",
        path: "/printcertificate",
        icon: <PrintOutlinedIcon />,
      },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    items: [
      isAdminOrPartnerOrAssistantOrManager() && {
        label: "Users",
        path: "/users",
        icon: <AdminPanelSettingsOutlinedIcon />,
      },
      isAdmin() && {
        label: "Organizations",
        path: "/organizations",
        icon: <BusinessOutlinedIcon />,
      },
      {
        label: "Schools",
        path: "/schools",
        icon: <SchoolOutlinedIcon />,
      },
    ],
  },
];

const isItemActive = (item, pathname) =>
  item.path === pathname ||
  Boolean(item.children?.some((child) => child.path === pathname));

export default function SideBar({ open, toggleDrawer }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sections = getNavigationSections()
    .map((section) => ({
      ...section,
      items: section.items.filter(Boolean),
    }))
    .filter((section) => section.items.length);
  const activeSectionId = sections.find((section) =>
    section.items.some((item) => isItemActive(item, location.pathname))
  )?.id;
  const [openSections, setOpenSections] = useState({
    competitions: true,
    "content-bank": true,
    participants: true,
    certificates: true,
    administration: true,
  });
  const [translationsOpen, setTranslationsOpen] = useState(
    ["/assigntranslation", "/translate", "/downloadcontestpaper"].includes(
      location.pathname
    )
  );

  useEffect(() => {
    if (
      ["/assigntranslation", "/translate", "/downloadcontestpaper"].includes(
        location.pathname
      )
    ) {
      setTranslationsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname &&
      activeSectionId &&
      activeSectionId !== "dashboard"
    ) {
      setOpenSections((current) => ({
        ...current,
        [activeSectionId]: true,
      }));
    }
  }, [activeSectionId, location.pathname]);

  const closeMobileDrawer = () => {
    if (isMobile) toggleDrawer(false);
  };

  const toggleSection = (sectionId) => {
    setOpenSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const handleNestedGroupClick = () => {
    if (!open) {
      toggleDrawer(true);
      setTranslationsOpen(true);
      return;
    }
    setTranslationsOpen((current) => !current);
  };

  return (
    <Drawer
      className={`simcc-sidebar ${open ? "is-expanded" : "is-rail"}`}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      expanded={open}
      mobile={isMobile}
      onClose={() => toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
    >
      <DrawerHeader className="simcc-sidebar__header">
        <Link
          to="/dashboard"
          className="simcc-sidebar__brand"
          onClick={closeMobileDrawer}
          aria-label="SIMCC dashboard"
        >
          <img src={simccLogo} alt="" className="simcc-sidebar__logo" />
          {open && (
            <span className="simcc-sidebar__brand-copy">
              <strong>SIMCC</strong>
              <small>Management Portal</small>
            </span>
          )}
        </Link>
        <IconButton
          className="simcc-sidebar__collapse"
          onClick={() => toggleDrawer(!open)}
          aria-label={open ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={open}
          size="small"
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>

      <nav className="simcc-sidebar__nav" aria-label="Primary navigation">
        {sections.map((section) => (
          <section className="simcc-sidebar__section" key={section.label}>
            {!section.solo && open && (
              <button
                type="button"
                className="simcc-sidebar__section-title"
                onClick={() => toggleSection(section.id)}
                aria-expanded={Boolean(openSections[section.id])}
                aria-controls={`navigation-section-${section.id}`}
              >
                <span>{section.label}</span>
                {openSections[section.id] ? (
                  <ExpandMoreIcon aria-hidden="true" />
                ) : (
                  <ChevronRightIcon aria-hidden="true" />
                )}
              </button>
            )}
            {!section.solo && !open && (
              <div className="simcc-sidebar__divider" aria-hidden="true" />
            )}
            <Collapse
              in={section.solo || Boolean(openSections[section.id])}
              timeout="auto"
            >
              <List
                disablePadding
                id={`navigation-section-${section.id}`}
                className="simcc-sidebar__section-items"
              >
                {section.items.map((item) => {
                  const active = isItemActive(item, location.pathname);
                  const button = (
                    <ListItemButton
                      component={item.path ? Link : "button"}
                      to={item.path}
                      onClick={
                        item.children
                          ? handleNestedGroupClick
                          : closeMobileDrawer
                      }
                      selected={active}
                      className="simcc-sidebar__item"
                      aria-expanded={
                        item.children ? open && translationsOpen : undefined
                      }
                    >
                      <ListItemIcon className="simcc-sidebar__item-icon">
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          className="simcc-sidebar__item-text"
                          primary={item.label}
                        />
                      )}
                      {item.children && open &&
                        (translationsOpen ? (
                          <ExpandLessIcon className="simcc-sidebar__chevron" />
                        ) : (
                          <ExpandMoreIcon className="simcc-sidebar__chevron" />
                        ))}
                    </ListItemButton>
                  );

                  return (
                    <div key={item.label}>
                      <Tooltip
                        title={item.label}
                        placement="right"
                        disableHoverListener={open}
                        arrow
                      >
                        {button}
                      </Tooltip>
                      {item.children && (
                        <Collapse in={open && translationsOpen} timeout="auto">
                          <List
                            disablePadding
                            className="simcc-sidebar__subnav"
                          >
                            {item.children.map((child) => (
                              <ListItemButton
                                component={Link}
                                to={child.path}
                                onClick={closeMobileDrawer}
                                selected={location.pathname === child.path}
                                className="simcc-sidebar__subitem"
                                key={child.path}
                              >
                                <ListItemText primary={child.label} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </div>
                  );
                })}
              </List>
            </Collapse>
          </section>
        ))}
      </nav>
    </Drawer>
  );
}

SideBar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};
